import { Link, NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut, User as UserIcon, Layers, ShieldAlert, Truck, Store, PackageCheck, ShieldCheck, Menu } from "lucide-react";
import AIChatWidget from "@/components/AIChatWidget";
import InviteBusiness from "@/components/InviteBusiness";
import { useLanguage } from "@/i18n/LanguageContext";
import { LANGUAGES } from "@/i18n/translations";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useState } from "react";

const NAV = [
  { to: "/marketplace", key: "nav.marketplace", icon: Layers, testId: "nav-marketplace" },
  { to: "/heritage", key: "nav.heritage", icon: ShieldAlert, testId: "nav-heritage-db" },
  { to: "/directory", key: "nav.directory", icon: Truck, testId: "nav-service-directory" },
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { lang, change, t } = useLanguage();
  const nav = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);

  const roleNav = user && user !== false ? (
    user.role === "seller" ? { to: "/seller", key: "nav.seller", icon: Store, testId: "nav-seller-portal" } :
    user.role === "buyer" ? { to: "/buyer", key: "nav.orders", icon: PackageCheck, testId: "nav-buyer-portal" } :
    user.role === "service_provider" ? { to: "/directory/me", key: "nav.provider", icon: Truck, testId: "nav-provider-profile" } :
    user.role === "admin" ? { to: "/admin", key: "nav.admin", icon: ShieldCheck, testId: "nav-admin-console" } : null
  ) : null;

  return (
    <div className="min-h-screen flex flex-col bg-[color:var(--sand-base)]">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-xl border-b border-stone-200/80 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 lg:px-8 h-16 flex items-center justify-between gap-4">
          <Link to="/" data-testid="brand-link" className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-lg bg-[color:var(--forest-deep)] text-[color:var(--sand-base)] grid place-items-center font-bold heading">G</div>
            <div className="leading-none">
              <div className="heading text-lg font-extrabold text-stone-900">GeneticsTrade</div>
              <div className="text-[10px] tracking-wider uppercase text-stone-500 mono">Bio-Exchange · v1</div>
            </div>
          </Link>

          <nav className="hidden md:flex items-center gap-1">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testId}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${isActive ? "bg-[color:var(--sage-subtle)] text-[color:var(--forest-deep)]" : "text-stone-600 hover:bg-stone-100"}`
                }
              >
                <n.icon className="h-4 w-4" /> {t(n.key)}
              </NavLink>
            ))}
            {roleNav && (
              <NavLink
                to={roleNav.to}
                data-testid={roleNav.testId}
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${isActive ? "bg-[color:var(--sage-subtle)] text-[color:var(--forest-deep)]" : "text-stone-600 hover:bg-stone-100"}`
                }
              >
                <roleNav.icon className="h-4 w-4" /> {t(roleNav.key)}
              </NavLink>
            )}
            {user && user !== false && user.role === "seller" && user.accreditation_status === "verified" && (
              <NavLink
                to="/buyer"
                data-testid="nav-seller-purchases"
                className={({ isActive }) =>
                  `px-3 py-2 rounded-md text-sm font-medium flex items-center gap-1.5 transition-colors ${isActive ? "bg-[color:var(--sage-subtle)] text-[color:var(--forest-deep)]" : "text-stone-600 hover:bg-stone-100"}`
                }
              >
                <PackageCheck className="h-4 w-4" /> My Purchases
              </NavLink>
            )}
          </nav>

          <div className="flex items-center gap-2">
            <Select value={lang} onValueChange={change}>
              <SelectTrigger data-testid="language-select" className="w-[70px] h-8 text-xs"><SelectValue /></SelectTrigger>
              <SelectContent>
                {LANGUAGES.map((l) => <SelectItem key={l.code} value={l.code} data-testid={`lang-${l.code}`}>{l.flag}</SelectItem>)}
              </SelectContent>
            </Select>
            <div className="hidden md:block"><InviteBusiness /></div>
            {user && user !== false ? (
              <>
                <Link to="/profile" data-testid="header-profile-link" className="hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full border border-stone-200 hover:bg-stone-50 max-w-[240px]">
                  <UserIcon className="h-4 w-4 text-stone-600 shrink-0" />
                  <span className="text-sm text-stone-700 truncate whitespace-nowrap">{user.name || user.email}</span>
                  <span className="text-[10px] uppercase mono text-stone-500 border-l pl-2 ml-1 border-stone-200 shrink-0">{user.role}</span>
                </Link>
                <Button data-testid="header-logout-btn" variant="outline" size="sm" onClick={async () => { await logout(); nav("/"); }}>
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            ) : (
              <div className="hidden md:flex items-center gap-2">
                <Link to="/login"><Button data-testid="header-login-btn" variant="outline" size="sm">{t("nav.signin")}</Button></Link>
                <Link to="/register"><Button data-testid="header-register-btn" className="btn-earth" size="sm">{t("nav.register")}</Button></Link>
              </div>
            )}
            <button data-testid="header-menu-toggle" className="md:hidden p-2 rounded-md hover:bg-stone-100" onClick={() => setMenuOpen(!menuOpen)}>
              <Menu className="h-5 w-5" />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-stone-200 bg-white">
            <div className="p-3 flex flex-col gap-1">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} onClick={() => setMenuOpen(false)} data-testid={`mobile-${n.testId}`}
                      className="px-3 py-2 rounded-md text-sm text-stone-700 flex items-center gap-2 hover:bg-stone-100">
                  <n.icon className="h-4 w-4" /> {t(n.key)}
                </Link>
              ))}
              {roleNav && (
                <Link to={roleNav.to} onClick={() => setMenuOpen(false)} className="px-3 py-2 rounded-md text-sm text-stone-700 flex items-center gap-2 hover:bg-stone-100">
                  <roleNav.icon className="h-4 w-4" /> {t(roleNav.key)}
                </Link>
              )}
              {user && user !== false ? (
                <Link to="/profile" onClick={() => setMenuOpen(false)} data-testid="mobile-profile-link"
                      className="px-3 py-2 rounded-md text-sm text-stone-700 flex items-center gap-2 hover:bg-stone-100 border-t border-stone-100 mt-1 pt-3">
                  <UserIcon className="h-4 w-4" /> {user.name || user.email}
                </Link>
              ) : (
                <div className="flex gap-2 pt-3 border-t border-stone-100 mt-1">
                  <Link to="/login" onClick={() => setMenuOpen(false)} className="flex-1">
                    <Button data-testid="mobile-login-btn" variant="outline" size="sm" className="w-full">{t("nav.signin")}</Button>
                  </Link>
                  <Link to="/register" onClick={() => setMenuOpen(false)} className="flex-1">
                    <Button data-testid="mobile-register-btn" className="btn-earth w-full" size="sm">{t("nav.register")}</Button>
                  </Link>
                </div>
              )}
            </div>
          </div>
        )}
      </header>

      <main className="flex-1 bg-[color:var(--forest-deep)] text-[#EDF3EE]">{children}</main>
      <AIChatWidget />

      <footer className="mt-16 border-t border-stone-200 bg-white/60">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-10 grid md:grid-cols-3 gap-8 text-sm text-stone-600">
          <div>
            <div className="heading font-bold text-stone-800 mb-2">GeneticsTrade</div>
            <p>High-trust B2B marketplace for verified livestock genetic material — semen straws, embryos, and pedigree lineage traded under cold-chain custody and escrow protection.</p>
          </div>
          <div>
            <div className="heading font-semibold text-stone-800 mb-2">Trust & Compliance</div>
            <ul className="space-y-1">
              <li>USDA-APHIS accreditation workflow</li>
              <li>ICAR / Livestock Conservancy referenced</li>
              <li>Escrow-backed transactions</li>
              <li><Link to="/terms" data-testid="footer-terms-link" className="underline hover:text-stone-900">Terms &amp; Conditions — 6% sales fee</Link></li>
              <li><InviteBusiness triggerClassName="mt-2" /></li>
            </ul>
          </div>
          <div>
            <div className="heading font-semibold text-stone-800 mb-2">Contact</div>
            <p>admin@genetictrade.online</p>
            <p className="mono text-xs mt-2 text-stone-500">© 2026 GeneticsTrade — All bio-material shipments in LN₂ at −196°C</p>
          </div>
        </div>
      </footer>
    </div>
  );
}
